/**
 * ChordPro Editor
 *
 * CodeMirror-based editor with toolbar for section directives.
 * Provides syntax highlighting for ChordPro notation.
 */

import { chordpro } from "@bandwith/lezer-chordpro";
import { themeAlpha, themeColors } from "@bandwith/styles";
import { Button } from "@bandwith/ui";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { syntaxHighlighting } from "@codemirror/language";
import { EditorState, type Extension } from "@codemirror/state";
import { EditorView, keymap, placeholder } from "@codemirror/view";
import CodeMirror, {
  type ReactCodeMirrorRef,
  type ViewUpdate,
} from "@uiw/react-codemirror";
import { Search } from "lucide-react";
import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

// Lazy-load ChordFinder to reduce initial bundle (~310KB: chords-db, tonal, svguitar)
const ChordFinder = lazy(() =>
  import("./ChordFinder").then((m) => ({ default: m.ChordFinder })),
);

import { chordProHighlighting } from "./chordpro-theme";

/**
 * Bandwith-themed CodeMirror theme
 */
const bandwithTheme = EditorView.theme({
  "&": {
    backgroundColor: themeColors.bgSecondary,
    color: themeColors.textSecondary,
    fontSize: "14px",
    fontFamily:
      '"JetBrains Mono Variable", ui-monospace, SFMono-Regular, Menlo, monospace',
    minHeight: "inherit",
  },
  ".cm-content": {
    padding: "16px 20px",
    caretColor: themeColors.amber,
  },
  ".cm-cursor, .cm-dropCursor": {
    borderLeftColor: themeColors.amber,
    borderLeftWidth: "2px",
  },
  "&.cm-focused .cm-cursor": {
    borderLeftColor: themeColors.amber,
  },
  ".cm-activeLine": {
    backgroundColor: themeAlpha("amber", 0.05),
  },
  "&.cm-focused .cm-activeLine": {
    backgroundColor: themeAlpha("amber", 0.08),
  },
  ".cm-selectionBackground, ::selection": {
    backgroundColor: `${themeAlpha("amber", 0.2)} !important`,
  },
  "&.cm-focused .cm-selectionBackground": {
    backgroundColor: `${themeAlpha("amber", 0.25)} !important`,
  },
  ".cm-gutters": {
    backgroundColor: themeColors.bgSecondary,
    color: themeColors.textDisabled,
    border: "none",
    paddingRight: "8px",
  },
  ".cm-lineNumbers .cm-gutterElement": {
    padding: "0 8px 0 16px",
  },
  ".cm-activeLineGutter": {
    backgroundColor: themeAlpha("amber", 0.08),
    color: themeColors.textMuted,
  },
  ".cm-scroller": {
    overflow: "auto",
    backgroundColor: themeColors.bgSecondary,
    minHeight: "100%",
  },
  "&.cm-focused": {
    outline: "none",
  },
  ".cm-placeholder": {
    color: themeColors.textDisabled,
    fontStyle: "italic",
  },
});

type SectionType = "verse" | "chorus" | "bridge" | "comment";

const SECTION_DIRECTIVES: Record<SectionType, { start: string; end: string }> =
  {
    verse: { start: "{start_of_verse}", end: "{end_of_verse}" },
    chorus: { start: "{start_of_chorus}", end: "{end_of_chorus}" },
    bridge: { start: "{start_of_bridge}", end: "{end_of_bridge}" },
    comment: { start: "{comment: ", end: "}" },
  };

interface ChordProEditorProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  disabled?: boolean;
  className?: string;
  minHeight?: string;
}

export const ChordProEditor = memo(function ChordProEditor({
  value,
  onChange,
  onSubmit,
  disabled = false,
  className = "",
  minHeight = "256px",
}: ChordProEditorProps) {
  const editorRef = useRef<ReactCodeMirrorRef>(null);
  const [isChordFinderOpen, setIsChordFinderOpen] = useState(false);

  // Store onSubmit in a ref to avoid recreating extensions on every render
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  // Build extensions
  const extensions = useMemo<Extension[]>(
    () => [
      chordpro(),
      syntaxHighlighting(chordProHighlighting),
      history(),
      keymap.of([
        ...defaultKeymap,
        ...historyKeymap,
        // Cmd/Ctrl+Enter to submit form
        {
          key: "Mod-Enter",
          run: () => {
            onSubmitRef.current?.();
            return true;
          },
        },
      ]),
      placeholder(
        `Enter lyrics or notes here. ChordPro syntax is supported—examples:
[Am]Lyrics with [G]chords inline
{start_of_verse}...{end_of_verse}  {comment: Note to self}
Standard diagram: {chord: G}  Custom diagram: {define: GChill frets x 3 2 0 3 0}`,
      ),
      EditorView.lineWrapping,
      EditorState.readOnly.of(disabled),
    ],
    [disabled],
  );

  // Handle changes
  const handleChange = useCallback(
    (val: string, _viewUpdate: ViewUpdate) => {
      onChange(val);
    },
    [onChange],
  );

  // Insert text at cursor
  const insertText = useCallback(
    (text: string, options?: { cursorOffset?: number }) => {
      const view = editorRef.current?.view;
      if (!view) return;

      const { from, to } = view.state.selection.main;
      const transaction = view.state.update({
        changes: { from, to, insert: text },
        selection: {
          anchor: from + (options?.cursorOffset ?? text.length),
        },
      });
      view.dispatch(transaction);
      view.focus();
    },
    [],
  );

  // Wrap selection with section directives
  const wrapSelection = useCallback((type: SectionType) => {
    const view = editorRef.current?.view;
    if (!view) return;

    const { from, to } = view.state.selection.main;
    const selectedText = view.state.sliceDoc(from, to);
    const { start: startDir, end: endDir } = SECTION_DIRECTIVES[type];

    let newText: string;
    let cursorOffset: number;

    if (selectedText) {
      // Wrap selected text
      if (type === "comment") {
        newText = `${startDir}${selectedText}${endDir}`;
        cursorOffset = newText.length;
      } else {
        newText = `${startDir}\n${selectedText}\n${endDir}`;
        cursorOffset = newText.length;
      }
    } else {
      // No selection - insert empty section with cursor inside
      if (type === "comment") {
        newText = `${startDir}${endDir}`;
        cursorOffset = startDir.length;
      } else {
        newText = `${startDir}\n\n${endDir}`;
        cursorOffset = startDir.length + 1;
      }
    }

    const transaction = view.state.update({
      changes: { from, to, insert: newText },
      selection: { anchor: from + cursorOffset },
    });
    view.dispatch(transaction);
    view.focus();
  }, []);

  // Pointer-down handler for toolbar buttons.
  // Uses pointerdown + preventDefault instead of click so it fires before
  // mobile Safari blurs the editor and dismisses the keyboard.
  const handleToolbarPointerDown = useCallback(
    (type: SectionType) => (e: React.PointerEvent) => {
      e.preventDefault();
      wrapSelection(type);
    },
    [wrapSelection],
  );

  // Handle definition insertion from finder (at top of file)
  const handleInsertDefinition = useCallback((definitionText: string) => {
    const view = editorRef.current?.view;
    if (!view) return;

    // Insert at the beginning of the document
    const transaction = view.state.update({
      changes: { from: 0, to: 0, insert: `${definitionText}\n` },
    });
    view.dispatch(transaction);
    view.focus();
  }, []);

  return (
    <div className={`flex flex-col ${className}`} data-testid="notes-editor">
      {/* Toolbar - sticks to top */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 rounded-t-lg border border-b-0 border-border-subtle bg-bg-tertiary px-3 py-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsChordFinderOpen(true)}
          disabled={disabled}
          data-testid="find-chord-button"
        >
          <Search size={14} />
          Find Chord
        </Button>
        <div className="h-5 w-px bg-border-subtle" />
        <Button
          variant="ghost"
          size="sm"
          onPointerDown={handleToolbarPointerDown("verse")}
          disabled={disabled}
        >
          Verse
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onPointerDown={handleToolbarPointerDown("chorus")}
          disabled={disabled}
        >
          Chorus
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onPointerDown={handleToolbarPointerDown("bridge")}
          disabled={disabled}
        >
          Bridge
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onPointerDown={handleToolbarPointerDown("comment")}
          disabled={disabled}
        >
          Comment
        </Button>
      </div>

      {/* Editor */}
      <CodeMirror
        ref={editorRef}
        value={value}
        onChange={handleChange}
        theme={bandwithTheme}
        extensions={extensions}
        basicSetup={false}
        style={{ minHeight }}
        className="overflow-hidden rounded-b-lg rounded-t-none border border-border-subtle"
        aria-label="Notes"
      />

      {/* Chord Finder Modal - lazy-loaded to reduce initial bundle */}
      {isChordFinderOpen && (
        <Suspense fallback={null}>
          <ChordFinder
            isOpen={isChordFinderOpen}
            onClose={() => setIsChordFinderOpen(false)}
            onInsertChord={insertText}
            onInsertDefinition={handleInsertDefinition}
          />
        </Suspense>
      )}
    </div>
  );
});
